import {
  convertEnumFilterToApi,
  convertEnumFilterToArrayApi,
  convertStringFilterToApi,
  createDateRangeFilterConverter,
  convertBooleanFilterToApi,
  convertNumberFilterToApi
} from 'v5/platform/filters/filterConverters';
import { maybe, Maybe } from '@passionware/monads';
import {
  today,
  getLocalTimeZone,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  CalendarDate
} from '@internationalized/date';
import { CORE_FIELD_NAMES } from 'v5/features/list-filters/metastructure-filters/core/fields';
import { EnumFilter } from 'v5/api/_common/query/filters/EnumFilter';
import { StringFilter } from 'v5/api/_common/query/filters/StringFilter';
import { BooleanFilter } from 'v5/api/_common/query/filters/BooleanFilter';
import { NumberFilter } from 'v5/api/_common/query/filters/NumberFilter';
import { timestampFilter } from '../standard-date-filter/timestamp-filter';

// Date range conversion for production dates
const resolvePresetRange = (preset: string) => {
  const base = today(getLocalTimeZone());
  switch (preset) {
    case 'today':
      return { from: base, to: base };
    case 'tomorrow': {
      const tomorrow = base.add({ days: 1 });
      return { from: tomorrow, to: tomorrow };
    }
    case 'nextWeek': {
      const nextWeekStart = startOfWeek(base.add({ weeks: 1 }), 'en-US');
      const nextWeekEnd = endOfWeek(base.add({ weeks: 1 }), 'en-US');
      return { from: nextWeekStart, to: nextWeekEnd };
    }
    case 'nextMonth': {
      const nextMonthStart = startOfMonth(base.add({ months: 1 }));
      const nextMonthEnd = endOfMonth(base.add({ months: 1 }));
      return { from: nextMonthStart, to: nextMonthEnd };
    }
    default:
      return null;
  }
};

const calendarDateToTimestamp = (date: CalendarDate): number => {
  const jsDate = new Date(Date.UTC(date.year, date.month - 1, date.day));
  return Math.floor(jsDate.getTime() / 1000);
};

const convertProductionDateFilter = createDateRangeFilterConverter({
  startFieldName: 'production_start_date',
  endFieldName: 'production_end_date',
  resolvePreset: resolvePresetRange,
  dateToValue: calendarDateToTimestamp
});

/**
 * Unified mapping of all core field names to their converter functions
 * Supports core fields from all entity types
 */
export const coreFieldFiltersToHttp = {
  // Production fields
  [CORE_FIELD_NAMES.STATUS]: (filter: Maybe<EnumFilter<number>>) =>
    filter ? convertEnumFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.PRODUCTION_DATE]: convertProductionDateFilter,
  [CORE_FIELD_NAMES.CODE]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.ARCHIVED]: (filter: Maybe<BooleanFilter>) =>
    filter ? convertBooleanFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.USER_ID]: (filter: Maybe<EnumFilter<string>>) =>
    filter ? convertEnumFilterToApi(filter, (value: string) => value) : null,
  [CORE_FIELD_NAMES.PRODUCTION_TYPE_ID]: (filter: Maybe<EnumFilter<number>>) =>
    filter ? convertEnumFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.ICON]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.COLOR]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.TITLE]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.DESCRIPTION]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.BUDGET_AMOUNT]: (filter: Maybe<NumberFilter>) =>
    filter ? convertNumberFilterToApi(filter) : null,

  // Resource fields
  [CORE_FIELD_NAMES.RESOURCE_TYPE_ID]: (filter: Maybe<EnumFilter<number>>) =>
    filter ? convertEnumFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.ORGANISATION_ID]: (filter: Maybe<EnumFilter<number>>) =>
    filter ? convertEnumFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.REPRESENTED_BY_ID]: (filter: Maybe<EnumFilter<number>>) =>
    filter ? convertEnumFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.ROLE]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.TAGS]: (filter: Maybe<EnumFilter<string>>) =>
    filter ? convertEnumFilterToArrayApi(filter) : null,
  [CORE_FIELD_NAMES.GROUPS]: (filter: Maybe<EnumFilter<number>>) =>
    filter ? convertEnumFilterToArrayApi(filter) : null,
  [CORE_FIELD_NAMES.EMAILS]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.PHONES]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.WEBSITES]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.SOCIALS]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,
  [CORE_FIELD_NAMES.LOCATIONS]: (filter: Maybe<StringFilter>) =>
    filter ? convertStringFilterToApi(filter) : null,

  // Common fields
  [CORE_FIELD_NAMES.CREATED_AT]: timestampFilter.createHttpConverter(),
  [CORE_FIELD_NAMES.UPDATED_AT]: timestampFilter.createHttpConverter()
} as const;

/**
 * Mapping from frontend field names to API field names
 * Frontend uses camelCase, API expects snake_case
 * null = spread the converted result directly to the top level
 */
const fieldNameToApiMapping = {
  // Production fields that need mapping
  [CORE_FIELD_NAMES.PRODUCTION_TYPE_ID]: 'production_type_id',
  [CORE_FIELD_NAMES.PRODUCTION_DATE]: null, // null = spread converted result directly to top level (for date ranges)
  [CORE_FIELD_NAMES.USER_ID]: 'user_id',

  // Resource fields (already snake_case, but included for completeness)
  [CORE_FIELD_NAMES.RESOURCE_TYPE_ID]: 'resource_type_id',
  [CORE_FIELD_NAMES.ORGANISATION_ID]: 'organisation_id',
  [CORE_FIELD_NAMES.REPRESENTED_BY_ID]: 'represented_by_id',

  // Fields that don't need mapping (already snake_case or single word)
  [CORE_FIELD_NAMES.ROLE]: 'role',
  [CORE_FIELD_NAMES.TAGS]: 'tags',
  [CORE_FIELD_NAMES.GROUPS]: 'groups',
  [CORE_FIELD_NAMES.EMAILS]: 'emails',
  [CORE_FIELD_NAMES.PHONES]: 'phone_numbers',
  [CORE_FIELD_NAMES.WEBSITES]: 'websites',
  [CORE_FIELD_NAMES.SOCIALS]: 'socials',
  [CORE_FIELD_NAMES.LOCATIONS]: 'locations',
  [CORE_FIELD_NAMES.ICON]: 'icon',
  [CORE_FIELD_NAMES.COLOR]: 'color',
  [CORE_FIELD_NAMES.TITLE]: 'title',
  [CORE_FIELD_NAMES.DESCRIPTION]: 'description',
  [CORE_FIELD_NAMES.BUDGET_AMOUNT]: 'budget_amount',
  [CORE_FIELD_NAMES.STATUS]: 'status_id', // Special case: status -> status_id
  [CORE_FIELD_NAMES.CODE]: 'code',
  [CORE_FIELD_NAMES.ARCHIVED]: 'archived',
  [CORE_FIELD_NAMES.CREATED_AT]: 'created_at',
  [CORE_FIELD_NAMES.UPDATED_AT]: 'updated_at'
} as const;

/**
 * Convert core field filters to HTTP format
 * @param filters - Object containing core field filters (subset varies by entity)
 * @returns Object with core field filters converted to HTTP format
 */
export function convertCoreFieldFiltersToHttp(
  filters: Partial<Record<string, any>>
): Record<string, any> {
  const result: Record<string, any> = {};

  // Process all filters except 'archived' which is handled at a higher level
  Object.entries(filters).forEach(([fieldName, filterValue]) => {
    // Skip 'archived' as it's handled at the payload root level, not in filters
    if (fieldName === 'archived') {
      return;
    }

    // Check if this is a known core field
    if (fieldName in coreFieldFiltersToHttp) {
      if (maybe.isAbsent(filterValue)) {
        // If the filter value is absent, skip it
        return;
      }
      // Use the appropriate converter based on field name
      const converter =
        coreFieldFiltersToHttp[
          fieldName as keyof typeof coreFieldFiltersToHttp
        ];

      const converted = converter(filterValue);

      if (maybe.isPresent(converted)) {
        // Map frontend field name to API field name
        const apiFieldName = fieldNameToApiMapping[fieldName];

        // If apiFieldName is null, spread the converted result directly
        if (apiFieldName === null) {
          // Spread the filters directly into the result
          Object.assign(result, converted);
        } else {
          result[apiFieldName] = converted;
        }
      }
    }
  });

  return result;
}
